import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { CircularProgress } from '@mui/material';
import { Google as GoogleIcon } from '@mui/icons-material';

const LandingPage = () => {
  const { signInWithGoogle, user, loading } = useAuth();
  const navigate = useNavigate();
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleGoogleSignIn = async () => {
    try {
      setIsSigningIn(true);
      await signInWithGoogle();
    } catch (error) {
      console.error('Sign in error:', error);
    } finally {
      setIsSigningIn(false);
    }
  };

  useEffect(() => {
    if (user && !loading) {
      navigate('/home');
    }
  }, [user, loading, navigate]);

  return (
    <div className="font-sans text-slate-800 antialiased" style={{ backgroundColor: '#f8fafc', color: '#0f172a' }}>
      <style>{`
        :root { 
          --brand-900: #0f172a; 
          --muted: #64748b; 
        }
        body { 
          background-color: #f8fafc; 
          color: var(--brand-900); 
        }
        h1,h2,h3,h4 { 
          color: var(--brand-900); 
        }
        .rounded-image { 
          border-radius: 12px; 
        }
        .site-container { 
          max-width: 1152px; /* max-w-6xl equivalent */
          margin-left: auto; 
          margin-right: auto; 
        }
      `}</style>

      {/* Header */}
      <header className="border-b bg-white">
        <div className="site-container px-6 py-4 flex items-center justify-between">
          <a href="#" className="flex items-center gap-3 flex-shrink-0">
            <img src="/logo-icon.png" alt="Life Bricks" className="h-6 w-auto" />
            <span className="font-semibold text-lg">Life Bricks</span>
          </a>
          <div className="flex items-center gap-4">
            <nav className="hidden md:flex items-center gap-6 text-sm" aria-label="Main navigation">
            <a href="#features" className="hover:text-slate-600">Features</a>
            <a href="#about" className="hover:text-slate-600">About</a>
            <button
              onClick={handleGoogleSignIn}
              disabled={isSigningIn || loading}
              className="ml-4 inline-flex items-center rounded-md bg-slate-900 px-4 py-2 text-white text-sm disabled:opacity-50"
            >
              {isSigningIn ? (
                <>
                  <CircularProgress size={16} color="inherit" className="mr-2" />
                  Signing in...
                </>
              ) : (
                <>
                  <GoogleIcon className="mr-2" style={{ fontSize: '16px' }} />
                  Get started
                </>
              )}
            </button>
            </nav>
            <button 
              id="menu-toggle" 
              aria-expanded={mobileMenuOpen} 
              aria-controls="mobile-menu" 
              className="md:hidden p-2 flex-shrink-0"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              ☰<span className="sr-only">Open menu</span>
            </button>
          </div>
        </div>
      </header>

      {/* Mobile menu */}
      <div id="mobile-menu" className={`${mobileMenuOpen ? 'block' : 'hidden'} bg-white border-b md:hidden`}>
        <div className="px-6 py-4">
          <a href="#features" className="block py-2">Features</a>
          <a href="#about" className="block py-2">About</a>
          <button
            onClick={handleGoogleSignIn}
            disabled={isSigningIn || loading}
            className="block py-2 mt-2 inline-flex items-center rounded-md bg-slate-900 px-4 py-2 text-white disabled:opacity-50"
          >
            {isSigningIn ? (
              <>
                <CircularProgress size={16} color="inherit" className="mr-2" />
                Signing in...
              </>
            ) : (
              <>
                <GoogleIcon className="mr-2" style={{ fontSize: '16px' }} />
                Get started
              </>
            )}
          </button>
        </div>
      </div>

      <main>
        {/* Hero */}
        <section className="relative pt-6 pb-12 overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img src="image2_1.jpg" alt="" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-slate-900/70"></div>
          </div>
          <div className="site-container px-6 grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div className="relative z-10 -mt-4">
              <h1 className="text-3xl md:text-4xl font-extrabold leading-tight mb-3 text-white">Life Bricks</h1>
              <p className="text-base text-slate-200 mb-4">Your personal command center for life optimization. Real-time tracking, smart insights, and seamless organization across all aspects of your daily routine.</p>
              <div className="flex gap-4">
                <button
                  onClick={handleGoogleSignIn}
                  disabled={isSigningIn || loading}
                  className="inline-flex items-center rounded-md bg-slate-900 px-5 py-3 text-white shadow disabled:opacity-50"
                >
                  {isSigningIn ? (
                    <>
                      <CircularProgress size={16} color="inherit" className="mr-2" />
                      Signing in...
                    </>
                  ) : (
                    <>
                      <GoogleIcon className="mr-2" style={{ fontSize: '16px' }} />
                      Start building
                    </>
                  )}
                </button>
                <a href="#features" className="inline-flex items-center rounded-md border border-white px-5 py-3 text-white">Learn more</a>
              </div>
            </div>
            <div className="relative z-10 flex justify-center lg:justify-end">
              <div className="w-full max-w-sm bg-white/10 backdrop-blur-sm rounded-image p-4 shadow-2xl">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-white font-semibold">Daily Overview</h3>
                    <p className="text-slate-200 text-sm">Wednesday, Sept 27</p>
                  </div>
                  <div className="flex gap-2">
                    <button className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"></path>
                      </svg>
                    </button>
                    <button className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                      </svg>
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="rounded-lg bg-white/20 p-4">
                    <p className="text-slate-200 text-sm mb-1">Steps Today</p>
                    <div className="text-2xl font-bold text-white">8,439</div>
                    <p className="text-emerald-300 text-sm mt-1">+842 from yesterday</p>
                  </div>
                  <div className="rounded-lg bg-white/20 p-4">
                    <p className="text-slate-200 text-sm mb-1">Focus Time</p>
                    <div className="text-2xl font-bold text-white">4h 12m</div>
                    <p className="text-emerald-300 text-sm mt-1">89% of daily goal</p>
                  </div>
                </div>
                <div className="rounded-lg bg-white/20 p-4 mb-4">
                  <div className="flex justify-between items-center mb-4">
                    <h4 className="text-white font-medium">Weekly Progress</h4>
                    <button className="text-sm text-slate-200">View All</button>
                  </div>
                  <div className="flex items-end justify-between h-24 gap-2">
                  <div className="w-1/7 bg-white/30 rounded-t" style={{height: '60%'}}>
                      <div className="text-xs text-slate-200 text-center mt-2">M</div>
                    </div>
                    <div className="w-1/7 bg-white/30 rounded-t" style={{height: '45%'}}>
                      <div className="text-xs text-slate-200 text-center mt-2">T</div>
                    </div>
                    <div className="w-1/7 bg-white/30 rounded-t" style={{height: '80%'}}>
                      <div className="text-xs text-slate-200 text-center mt-2">W</div>
                    </div>
                    <div className="w-1/7 bg-white/50 rounded-t" style={{height: '75%'}}>
                      <div className="text-xs text-slate-200 text-center mt-2">T</div>
                    </div>
                    <div className="w-1/7 bg-white/30 rounded-t" style={{height: '65%'}}>
                      <div className="text-xs text-slate-200 text-center mt-2">F</div>
                    </div>
                    <div className="w-1/7 bg-white/30 rounded-t" style={{height: '40%'}}>
                      <div className="text-xs text-slate-200 text-center mt-2">S</div>
                    </div>
                    <div className="w-1/7 bg-white/30 rounded-t" style={{height: '50%'}}>
                      <div className="text-xs text-slate-200 text-center mt-2">S</div>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <button className="rounded-lg bg-white/20 p-3 text-center">
                    <svg className="w-6 h-6 text-white mx-auto mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"></path>
                    </svg>
                    <span className="text-xs text-slate-200">Stats</span>
                  </button>
                  <button className="rounded-lg bg-white/20 p-3 text-center">
                    <svg className="w-6 h-6 text-white mx-auto mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                    </svg>
                    <span className="text-xs text-slate-200">Timer</span>
                  </button>
                  <button className="rounded-lg bg-white/20 p-3 text-center">
                    <svg className="w-6 h-6 text-white mx-auto mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"></path>
                    </svg>
                    <span className="text-xs text-slate-200">Tasks</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features / Trackers */}
        <section id="features" className="py-16">
          <div className="site-container px-6">
            <h2 className="text-2xl font-bold mb-6">Everything You Need to Stay Productive</h2>
            <p className="text-slate-600 mb-8">Comprehensive tracking tools designed for modern professionals who want to optimize every aspect of their lives.</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
              <div className="p-6 bg-white rounded-lg shadow-sm">
                <h3 className="font-semibold mb-2">Real-time Data</h3>
                <p className="text-sm text-slate-600">Live updates so you can react instantly.</p>
              </div>
              <div className="p-6 bg-white rounded-lg shadow-sm">
                <h3 className="font-semibold mb-2">AI Analytics</h3>
                <p className="text-sm text-slate-600">Actionable insights to inform better decisions.</p>
              </div>
              <div className="p-6 bg-white rounded-lg shadow-sm">
                <h3 className="font-semibold mb-2">Cross-platform</h3>
                <p className="text-sm text-slate-600">Access your data anywhere, anytime.</p>
              </div>
            </div>

            <h3 className="text-xl font-bold mb-4">Trackers</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="p-4 bg-white rounded-lg shadow-sm">
                <h4 className="font-semibold">Stocks Watcher</h4>
                <p className="text-sm text-slate-600">Real-time market monitoring</p>
              </div>
              <div className="p-4 bg-white rounded-lg shadow-sm">
                <h4 className="font-semibold">News Tracker</h4>
                <p className="text-sm text-slate-600">Stay informed with latest news</p>
              </div>
              <div className="p-4 bg-white rounded-lg shadow-sm">
                <h4 className="font-semibold">Fitness Tracker</h4>
                <p className="text-sm text-slate-600">Track workouts and progress</p>
              </div>
              <div className="p-4 bg-white rounded-lg shadow-sm">
                <h4 className="font-semibold">Food Tracker</h4>
                <p className="text-sm text-slate-600">Monitor nutrition and meals</p>
              </div>
              <div className="p-4 bg-white rounded-lg shadow-sm">
                <h4 className="font-semibold">Notes Tracker</h4>
                <p className="text-sm text-slate-600">Organize thoughts and ideas</p>
              </div>
              <div className="p-4 bg-white rounded-lg shadow-sm">
                <h4 className="font-semibold">Sleep Tracker</h4>
                <p className="text-sm text-slate-600">Optimize your rest patterns</p>
              </div>
              <div className="p-4 bg-white rounded-lg shadow-sm">
                <h4 className="font-semibold">Books Tracker</h4>
                <p className="text-sm text-slate-600">Manage your reading list</p>
              </div>
              <div className="p-4 bg-white rounded-lg shadow-sm">
                <h4 className="font-semibold">Task Manager</h4>
                <p className="text-sm text-slate-600">AI-powered productivity</p>
              </div>
            </div>
          </div>
        </section>

        {/* Visual section */}
        <section className="py-16 bg-slate-50">
          <div className="site-container px-6 grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="text-xl font-bold mb-4">Beautiful, actionable dashboards</h3>
              <p className="text-slate-600 mb-6">Quickly see trends, insights, and the next actions that matter. Built for busy people who want meaningful progress.</p>
            </div>
            <div>
              <img src="image2.jpg" srcSet="image2.jpg 800w, image2.jpg 1200w" alt="App screenshot" className="w-full rounded-image shadow-md object-cover" loading="lazy" decoding="async" width="800" height="500" />
            </div>
          </div>
        </section>

        {/* Built for / Values / CTA */}
        <section className="py-16 bg-white">
          <div className="site-container px-6">
            <h2 className="text-2xl font-bold mb-4">Built for Tech Professionals</h2>
            <p className="text-slate-600 mb-6">Designed specifically for developers, designers, and tech professionals who need to stay organized and productive. Build your life brick by brick - track your stocks, monitor market news, manage your fitness goals, and organize your tasks all in one place.</p>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
              <div className="p-4 bg-slate-50 rounded-lg text-center">
                <h4 className="font-semibold">Real-time Data</h4>
              </div>
              <div className="p-4 bg-slate-50 rounded-lg text-center">
                <h4 className="font-semibold">AI Analytics</h4>
              </div>
              <div className="p-4 bg-slate-50 rounded-lg text-center">
                <h4 className="font-semibold">Cross-platform</h4>
              </div>
              <div className="p-4 bg-slate-50 rounded-lg text-center">
                <h4 className="font-semibold">Secure</h4>
              </div>
            </div>

            <div className="text-center">
              <h3 className="text-xl font-bold mb-2">Ready to Optimize Your Life?</h3>
              <p className="text-slate-600 mb-6">Start building your organized and productive life with our comprehensive life management platform.</p>
              <button
                onClick={handleGoogleSignIn}
                disabled={isSigningIn || loading}
                className="inline-flex items-center rounded-md bg-slate-900 px-6 py-3 text-white disabled:opacity-50"
              >
                {isSigningIn ? (
                  <>
                    <CircularProgress size={16} color="inherit" className="mr-2" />
                    Signing in...
                  </>
                ) : (
                  <>
                    <GoogleIcon className="mr-2" style={{ fontSize: '16px' }} />
                    Sign in
                  </>
                )}
              </button>
              <p className="text-xs text-slate-500 mt-4">By signing in, you agree to our Terms of Service and Privacy Policy</p>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="border-t bg-white">
          <div className="site-container px-6 py-10 grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <a href="#" className="flex items-center gap-3 text-slate-900 hover:text-slate-700">
                <img src="/logo-icon.png" alt="Life Bricks" className="h-6 w-auto" />
                <span className="font-semibold">Life Bricks</span>
              </a>
              <p className="text-sm text-slate-600 mt-4">Built with care. Designed for intentional living.</p>
            </div>
            <div>
              <h5 className="font-semibold mb-2 text-slate-900">Resources</h5>
              <ul className="text-sm text-slate-600 space-y-2">
                <li><a href="#about" className="text-slate-600 hover:text-slate-800">About</a></li>
                <li><a href="#help" className="text-slate-600 hover:text-slate-800">Help</a></li>
                <li><a href="#features" className="text-slate-600 hover:text-slate-800">Features</a></li>
              </ul>
            </div>
            <div>
              <h5 className="font-semibold mb-2 text-slate-900">Support</h5>
              <ul className="text-sm text-slate-600 space-y-2">
                <li><a href="https://lifebricks.slack.com/" target="_blank" rel="noopener noreferrer" className="text-slate-600 hover:text-slate-800">Contact</a></li>
                <li><a href="#faq" className="text-slate-600 hover:text-slate-800">FAQ</a></li>
                <li><a href="https://buymeacoffee.com/lifebricks.ai" target="_blank" rel="noopener noreferrer" className="text-slate-600 hover:text-slate-800">Contributions</a></li>
              </ul>
            </div>
          </div>
          <div className="text-center text-sm text-slate-500 py-4">© 2025 Life Bricks. All rights reserved.</div>
          <div className="site-container px-6 text-xs text-slate-500 py-4">
            <p>Disclaimer: All AI-generated content is for advisory purposes only and should not be considered medical, financial, or professional advice.</p>
            <p className="mt-2">Licensed under the GNU General Public License v3.0. Contributions welcome 🎉</p>
            <div className="mt-4 flex justify-center">
              <a 
                href="https://buymeacoffee.com/lifebricks.ai" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-block"
              >
                <img 
                  src="https://cdn.buymeacoffee.com/buttons/v2/default-yellow.png" 
                  alt="Buy Me A Coffee" 
                  className="h-12 w-auto hover:opacity-80 transition-opacity"
                />
              </a>
            </div>
          </div>
        </footer>
      </main>
    </div>
  );
};

export default LandingPage;
